/** Barras da Expedição: por etapa (uma barra por situação do Tiny, com "Enviado" separado do "Embalado") e por canal
 * (segmentos por etapa + legenda + "faltam"). */
import { Box, CardActionArea, Stack, Typography } from '@mui/material'
import type { AbrirLista, Pedido } from './types'
import { CANAIS, ROTULO_CANAL } from './types'
import { AVISO_DECORA, COR_ETAPA, ORDEM_ETAPA, ROTULO_ETAPA, doEtapa, faltam, resumoPedidos, type Etapa } from './logic'

/** Largura mínima de um pedaço com valor > 0 (senão o pedaço de 1 pedido some ao lado de 300). */
const MIN_PEDACO = 40

/** Barra única dividida por situação. Sem números dentro (ficam ilegíveis em pedaço pequeno): os números estão na
 * legenda abaixo (LegendaSituacoes). Pedaço com valor > 0 tem pelo menos 40px (ou a parte igual da barra, se
 * não couber: num celular estreito 4 × 40px passaria do espaço). */
export function BarraSegmentada({ total, situacoes = ORDEM_ETAPA, contagem, onClick, altura = 50, largura = 100 }: {
  total: number
  situacoes?: Etapa[]
  contagem: (s: Etapa) => number
  onClick: (s: Etapa) => void
  altura?: number
  /** % da largura disponível (barra proporcional ao maior canal). */
  largura?: number
}) {
  const pedacos = total ? situacoes.map((s) => [s, contagem(s)] as const).filter(([, n]) => n > 0) : []
  return (
    <Box sx={{ display: 'flex', height: altura, width: `${largura}%`, minWidth: `min(100%, ${pedacos.length * MIN_PEDACO}px)`, borderRadius: 2.5, overflow: 'hidden', bgcolor: 'action.hover' }}>
      {pedacos.map(([s, n]) => (
        <Box key={s} role="button" tabIndex={0} title={`${ROTULO_ETAPA[s]}: ${n}`} aria-label={`${ROTULO_ETAPA[s]}: ${n}`}
          onClick={() => onClick(s)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(s) } }}
          sx={{
            flex: `${n} 1 0px`, minWidth: `min(${MIN_PEDACO}px, ${100 / pedacos.length}%)`, bgcolor: COR_ETAPA[s], cursor: 'pointer',
            borderRight: '2px solid #fff', '&:last-of-type': { borderRight: 0 }, '&:hover': { filter: 'brightness(1.12)' }, '&:focus-visible': { outline: '3px solid #0f172a', outlineOffset: -3 },
          }} />
      ))}
    </Box>
  )
}

/** Legenda fixa da barra: "● Embalado 379 · ● Separado 2 · ● A separar 18" (só o que tem pedido). Cada item abre a lista. */
export function LegendaSituacoes({ situacoes = ORDEM_ETAPA, contagem, onClick, tamanho = 13 }: {
  situacoes?: Etapa[]
  contagem: (s: Etapa) => number
  onClick: (s: Etapa) => void
  tamanho?: number
}) {
  const itens = situacoes.map((s) => [s, contagem(s)] as const).filter(([, n]) => n > 0)
  if (!itens.length) return null
  return (
    <Stack direction="row" columnGap={1.5} rowGap={0.25} flexWrap="wrap" useFlexGap>
      {itens.map(([s, n]) => (
        <CardActionArea key={s} onClick={() => onClick(s)} sx={{ width: 'auto', borderRadius: 1.5, px: 0.5, py: 0.25 }}>
          <Stack direction="row" spacing={0.6} alignItems="center">
            <Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: COR_ETAPA[s], flexShrink: 0 }} />
            <Typography sx={{ fontSize: tamanho, lineHeight: 1.3 }} color="text.secondary">
              {ROTULO_ETAPA[s]} <Box component="b" sx={{ color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>{n}</Box>
            </Typography>
          </Stack>
        </CardActionArea>
      ))}
    </Stack>
  )
}

/** Linha de apoio embaixo do nome da etapa. */
const LEGENDA_ETAPA: Partial<Record<Etapa, string>> = {
  0: 'ML ainda não liberou', 2: 'falta embalar', up_a_processar: 'Shopee Decora', up_etiqueta: 'Decora · não é embalado',
  3: 'aguardando coleta', enviado: 'já saiu',
}

/** `etapas` = etapasVisiveis(dados.pedidos): "Aguardando liberação" e as da Decora só aparecem quando existem. */
export function BarrasSituacao({ pedidos, sufixo, abrirLista, etapas = ORDEM_ETAPA }: {
  pedidos: Pedido[]; sufixo: string; abrirLista: AbrirLista; etapas?: Etapa[]
}) {
  const grupos = etapas.map((s) => [s, doEtapa(pedidos, s)] as const)
  const max = Math.max(1, ...grupos.map(([, ps]) => ps.length))
  return (
    <Stack spacing={0.5}>
      {grupos.map(([s, ps]) => (
        <CardActionArea key={s} onClick={() => abrirLista(`${ROTULO_ETAPA[s]} · ${sufixo}`, ps)} sx={{ borderRadius: 2, px: 0.75, py: 0.5 }}>
          <Stack direction="row" spacing={1.75} alignItems="center">
            <Box sx={{ width: { xs: 120, sm: 190 }, flexShrink: 0 }}>
              <Typography sx={{ font: '700 20px Outfit', lineHeight: 1.05 }}>{ROTULO_ETAPA[s].toUpperCase()}</Typography>
              {LEGENDA_ETAPA[s] && <Typography variant="caption" color="text.secondary">{LEGENDA_ETAPA[s]}</Typography>}
            </Box>
            <Box sx={{ flex: 1, height: 46, bgcolor: 'action.hover', borderRadius: 2.5, overflow: 'hidden' }}>
              <Box sx={{ height: '100%', width: `${(ps.length / max) * 100}%`, bgcolor: COR_ETAPA[s], borderRadius: 2.5 }} />
            </Box>
            <Typography sx={{ width: 100, textAlign: 'right', font: '800 40px Outfit', lineHeight: 1, color: COR_ETAPA[s], fontVariantNumeric: 'tabular-nums' }}>
              {ps.length}
            </Typography>
          </Stack>
        </CardActionArea>
      ))}
    </Stack>
  )
}

export function BarrasCanal({ pedidos, sufixo, abrirLista }: { pedidos: Pedido[]; sufixo: string; abrirLista: AbrirLista }) {
  const linhas = CANAIS.map((c) => [c, pedidos.filter((p) => p.canal === c)] as const).filter(([, ps]) => ps.length)
  const max = Math.max(1, ...linhas.map(([, ps]) => ps.length))
  if (!linhas.length) return <Typography color="text.secondary" sx={{ py: 2 }}>Nenhum pedido com esses filtros.</Typography>
  return (
    <Stack spacing={1}>
      {linhas.map(([c, ps]) => {
        const rot = ROTULO_CANAL[c]
        const pend = faltam(ps)
        const doS = (s: Etapa) => doEtapa(ps, s)
        const conta = (s: Etapa) => doS(s).length
        return (
          <Box key={c}>
            <Stack direction="row" spacing={1.75} alignItems="center">
              <CardActionArea onClick={() => abrirLista(`${rot} · ${sufixo}`, ps)} sx={{ width: { xs: 110, sm: 190 }, flexShrink: 0, borderRadius: 2, px: 0.75, py: 0.5 }}>
                <Typography sx={{ font: '700 18px Outfit', lineHeight: 1.1 }}>{rot}</Typography>
                <Typography variant="caption" color="text.secondary" component="div">{resumoPedidos(ps)}</Typography>
                {c === 'shopee_decora' && <Typography variant="caption" component="div" sx={{ color: '#be185d', fontWeight: 600, lineHeight: 1.2 }}>{AVISO_DECORA}</Typography>}
              </CardActionArea>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <BarraSegmentada total={ps.length} largura={(ps.length / max) * 100} altura={36} contagem={conta}
                  onClick={(s) => abrirLista(`${rot} · ${ROTULO_ETAPA[s]} · ${sufixo}`, doS(s))} />
              </Box>
              <CardActionArea onClick={() => abrirLista(`${rot} · faltam embalar · ${sufixo}`, pend)} sx={{ width: { xs: 64, sm: 110 }, flexShrink: 0, borderRadius: 2, textAlign: 'right', px: 0.75 }}>
                <Typography sx={{ font: '800 32px Outfit', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{pend.length}</Typography>
                <Typography variant="caption" color="text.secondary">faltam</Typography>
              </CardActionArea>
            </Stack>
            {/* legenda fixa: embaixo da barra no computador; a linha inteira no celular */}
            <Box sx={{ pl: { xs: 0.25, sm: '204px' }, mt: 0.25 }}>
              <LegendaSituacoes tamanho={12} contagem={conta}
                onClick={(s) => abrirLista(`${rot} · ${ROTULO_ETAPA[s]} · ${sufixo}`, doS(s))} />
            </Box>
          </Box>
        )
      })}
    </Stack>
  )
}
